import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowsClockwiseIcon,
  CalendarCheckIcon,
  CheckCircleIcon,
  CopyIcon,
  LinkBreakIcon,
  PlugsIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import {
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  StatusChip,
  fmtRelativeTime,
} from '@smena/ui';
import { embedCard, embedContent, embedHeader, type EmbeddableCardProps } from './embed';
import { connectRc, disconnectRc, fetchRcStatus, rotateRcSecret } from './api';

/**
 * «RealtyCalendar»: выдаёт УК готовую ссылку для кабинета RC.
 *
 * Единственный self-serve путь подключения: RC не умеет ни OAuth, ни header-auth - клиент
 * вставляет наш URL в «Настройки → CRM-интеграции», а секрет едет в query. Значит ссылка
 * персональная (account + secret) и её надо где-то показать - до этой карточки URL умел
 * собирать только бэкенд, а панель отправляла человека в несуществующий раздел.
 *
 * Три состояния, а не два: «не подключено» (ссылки нет, есть кнопка), «ждём первое событие»
 * (ссылка выдана, но по ней ещё не пришло ни одной брони) и «принимаем события». Раньше
 * первого не существовало: GET статуса создавал интеграцию сам, и поллинг раз в 5 секунд
 * возвращал только что отключённый RC с новым секретом.
 *
 * Ссылка = секрет: показываем предупреждение и даём ротацию на случай утечки.
 */
export function RealtyCalendarCard({ embedded }: EmbeddableCardProps = {}) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  // Оба действия необратимы, поэтому идут через подтверждение. Одно состояние вместо двух
  // флагов - чтобы нельзя было открыть обе плашки разом и подтвердить не то, что читал.
  const [confirm, setConfirm] = useState<'none' | 'rotate' | 'disconnect'>('none');

  const q = useQuery({
    queryKey: ['rc-status'],
    queryFn: fetchRcStatus,
    // Поллим только пока ждём первое событие: человек сидит в кабинете RC и вставляет ссылку,
    // статус должен позеленеть сам, без F5. Без интеграции поллинг не нужен - без действия
    // человека там ничего не изменится.
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d?.integration) return false;
      return d.integration.eventsCount > 0 ? 60_000 : 5_000;
    },
  });

  const rotateMut = useMutation({
    mutationFn: rotateRcSecret,
    onSuccess: () => {
      setConfirm('none');
      toast.success('Секрет обновлён - вставьте новую ссылку в кабинет RealtyCalendar');
      void qc.invalidateQueries({ queryKey: ['rc-status'] });
      void qc.invalidateQueries({ queryKey: ['integrations-health'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось обновить секрет'),
  });

  const connectMut = useMutation({
    mutationFn: connectRc,
    onSuccess: (status) => {
      // Кладём ответ в кэш сразу: ссылка появляется в тот же кадр, а не после рефетча.
      qc.setQueryData(['rc-status'], status);
      toast.success('Ссылка готова - вставьте её в кабинете RealtyCalendar');
      void qc.invalidateQueries({ queryKey: ['integrations-health'] });
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : 'Не удалось подключить RealtyCalendar'),
  });

  const disconnectMut = useMutation({
    mutationFn: disconnectRc,
    onSuccess: (r) => {
      setConfirm('none');
      toast.success(
        r.mappingsRemoved > 0
          ? `RealtyCalendar отключён, привязок объектов удалено: ${r.mappingsRemoved}`
          : 'RealtyCalendar отключён - брони по старой ссылке больше не принимаем',
      );
      void qc.invalidateQueries({ queryKey: ['rc-status'] });
      void qc.invalidateQueries({ queryKey: ['integrations-health'] });
      void qc.invalidateQueries({ queryKey: ['rc-unmapped'] });
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : 'Не удалось отключить RealtyCalendar'),
  });

  const s = q.data;
  // Три независимые оси, а не две: заведена ли интеграция, идут ли события и становятся ли
  // они бронями. Раньше осей было две, и «принимаем события» зеленело на потоке ошибок:
  // events_count рос на любой доставке, включая нераспарсенные.
  const integration = s?.integration ?? null;
  const events = integration?.eventsCount ?? 0;
  const booked = integration?.bookingsCount ?? 0;
  const errors = integration?.errorsCount ?? 0;
  const receiving = booked > 0;
  // События есть, броней нет - худшее состояние: снаружи всё выглядит рабочим.
  const stuck = events > 0 && booked === 0;
  // RC останавливает вебхук после двух суток недоставки, и включается он только руками
  // в кабинете. Наружу мы всегда отвечаем 200, так что тишина - единственный признак.
  const silentDays =
    integration?.lastEventAt && events > 0
      ? Math.floor((Date.now() - Date.parse(integration.lastEventAt)) / 86_400_000)
      : 0;
  const silent = silentDays >= 7;
  const busy = rotateMut.isPending || disconnectMut.isPending;

  async function copyUrl() {
    if (!s?.webhookUrl) return;
    try {
      await navigator.clipboard.writeText(s.webhookUrl);
      toast.success('Ссылка скопирована');
    } catch {
      // Clipboard API недоступен без https/permissions - подставляем выделение руками.
      inputRef.current?.select();
      toast.error('Не удалось скопировать - ссылка выделена, скопируйте вручную');
    }
  }

  return (
    <Card className={embedCard(embedded)}>
      <CardHeader className={embedHeader(embedded, 'flex flex-row items-center justify-between gap-3')}>
        <CardTitle className="flex items-center gap-2">
          <CalendarCheckIcon weight="bold" className="size-4 text-accent-fg" /> RealtyCalendar
        </CardTitle>
        {s ? (
          <StatusChip
            tone={!integration ? 'muted' : silent ? 'red' : receiving ? 'green' : 'amber'}
          >
            {!integration
              ? 'не подключено'
              : silent
                ? `тишина ${silentDays} дн.`
                : receiving
                  ? 'принимаем брони'
                  : stuck
                    ? 'события есть, броней нет'
                    : 'ждём первое событие'}
          </StatusChip>
        ) : null}
      </CardHeader>

      <CardContent className={embedContent(embedded, 'flex flex-col gap-3 text-sm')}>
        {q.isLoading ? (
          <p className="text-muted">Загружаем…</p>
        ) : q.isError || !s ? (
          <div className="flex items-start gap-2 rounded-md border border-red/40 bg-red/10 p-3 text-xs text-red">
            <WarningIcon weight="fill" className="mt-0.5 size-4 shrink-0" />
            <span>
              Не удалось получить статус:{' '}
              {q.error instanceof ApiError ? q.error.message : 'ошибка сервера'}.
            </span>
          </div>
        ) : !integration ? (
          // Не подключено: ссылки не существует, показывать нечего - только объяснить, что
          // произойдёт по кнопке. Секрет заводится здесь и больше нигде.
          <>
            <p className="text-muted">
              Брони из RealtyCalendar приходят к нам вебхуком: гость, телефон, даты, суммы, отмены.
              Ссылку вставляет сам клиент - токен и наш доступ в его кабинет не нужны.
            </p>
            <div className="flex items-start gap-2 rounded-md border border-line bg-panel2/40 p-3 text-xs text-muted">
              <PlugsIcon weight="bold" className="mt-0.5 size-4 shrink-0" />
              <span>
                Интеграция не подключена: брони из RealtyCalendar к нам не идут. По кнопке ниже
                мы выдадим персональную ссылку - её нужно будет вставить в кабинете RC, в{' '}
                <span className="text-text">Настройки → Интеграции → Webhooks</span>.
              </span>
            </div>
            {/* Про платность RC говорим до подключения, а не после: списания начнутся у
                клиента, и узнать о них он должен от нас, а не из выписки. */}
            <p className="text-[11px] text-muted">
              У RealtyCalendar интеграция платная: списания по тарифу «за интеграцию с внешними
              системами» (тарифы - на странице «Баланс» кабинета RC).
            </p>
            <div>
              <Button size="sm" disabled={connectMut.isPending} onClick={() => connectMut.mutate()}>
                <PlugsIcon weight="bold" className="size-4" />
                {connectMut.isPending ? 'Подключаем…' : 'Подключить'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-muted">
              Брони из RealtyCalendar приходят к нам вебхуком: гость, телефон, даты, суммы, отмены.
              Ссылку вставляет сам клиент - токен и наш доступ в его кабинет не нужны.
            </p>

            <div className="flex flex-col gap-2 rounded-md border border-line bg-panel2/40 p-3">
              <div className="text-[11px] uppercase tracking-wider text-muted">
                Ссылка для кабинета RealtyCalendar
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  ref={inputRef}
                  readOnly
                  aria-label="Webhook-ссылка для кабинета RealtyCalendar"
                  value={s.webhookUrl ?? ''}
                  onFocus={(e) => e.currentTarget.select()}
                  className="font-mono text-xs"
                />
                <Button size="sm" className="shrink-0" onClick={() => void copyUrl()}>
                  <CopyIcon weight="bold" className="size-4" /> Скопировать
                </Button>
              </div>
              <ol className="list-inside list-decimal space-y-1 text-xs text-muted">
                <li>
                  Откройте в кабинете RC{' '}
                  <a
                    href="https://realtycalendar.ru/settings/#/integration"
                    target="_blank"
                    rel="noreferrer"
                    className="text-accent-fg underline underline-offset-2"
                  >
                    Настройки → Интеграции
                  </a>
                  , секция <span className="text-text">Webhooks</span>
                </li>
                <li>
                  Вставьте ссылку в поле <span className="text-text">«Введите URL нового WebHook»</span> и
                  нажмите <span className="text-text">«Добавить WebHook»</span>
                </li>
                <li>
                  Дождаться первой брони - статус здесь позеленеет сам. Чтобы не ждать, измените
                  любую будущую бронь в RC - событие придёт сразу
                </li>
              </ol>
              {/* RC берёт деньги за webhooks - клиент увидит списания и должен узнать об этом
                  от нас, а не из выписки. */}
              <p className="text-[11px] text-muted">
                У RealtyCalendar интеграция платная: списания по тарифу «за интеграцию с внешними
                системами» (тарифы - на странице «Баланс» кабинета RC).
              </p>
            </div>

            {/* Тишина важнее секрета и ошибок: если вебхук остановлен, всё остальное уже
                неактуально, а сам RC об этом не скажет. */}
            {silent ? (
              <div className="flex items-start gap-2 rounded-md border border-red/40 bg-red/10 p-3 text-xs text-red">
                <WarningIcon weight="fill" className="mt-0.5 size-4 shrink-0" />
                <div className="flex flex-col gap-1">
                  <span>
                    От RealtyCalendar ничего не приходило {silentDays} дн. Если за это время
                    брони менялись, вебхук у них остановлен.
                  </span>
                  <span>
                    Проверьте в кабинете RC (Настройки → Интеграции → Webhooks): рядом с адресом
                    должно быть «Данные на WebHooks отправляются». Если написано «Остановлено» -
                    нажмите «Запустить», само оно не включится.
                  </span>
                </div>
              </div>
            ) : null}

            {stuck ? (
              <div className="flex items-start gap-2 rounded-md border border-amber/40 bg-amber/10 p-3 text-xs text-amber">
                <WarningIcon weight="fill" className="mt-0.5 size-4 shrink-0" />
                <span>
                  События от RealtyCalendar приходят ({events}), но ни одна бронь не создана.
                  Скорее всего дело в объекте: посмотрите ленту «Что приходило от источников»
                  внизу страницы.
                </span>
              </div>
            ) : null}

            {errors > 0 && integration.lastErrorReason ? (
              <div className="flex items-start gap-2 rounded-md border border-line bg-panel2/40 p-3 text-xs text-muted">
                <WarningIcon weight="bold" className="mt-0.5 size-4 shrink-0 text-amber" />
                <div className="flex flex-col gap-0.5">
                  <span>
                    Не доехало событий: <span className="tabular-nums text-text">{errors}</span>
                    {integration.lastErrorAt ? (
                      <>, последнее {fmtRelativeTime(integration.lastErrorAt)}</>
                    ) : null}
                  </span>
                  <span className="text-text">{integration.lastErrorReason}</span>
                </div>
              </div>
            ) : null}

            <div className="flex items-start gap-2 rounded-md border border-amber/40 bg-amber/10 p-3 text-xs text-amber">
              <WarningIcon weight="fill" className="mt-0.5 size-4 shrink-0" />
              <span>
                В ссылке зашит секрет - она равносильна праву присылать нам брони от имени этого
                аккаунта. Отправляйте её только клиенту, не публикуйте.
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
              {receiving ? (
                <span className="flex items-center gap-1.5 text-green">
                  <CheckCircleIcon weight="fill" className="size-3.5" />
                  Броней принято: <span className="tabular-nums">{booked}</span>
                </span>
              ) : (
                <span>Броней пока нет</span>
              )}
              {events > 0 ? <span>Событий всего: {events}</span> : null}
              {integration.lastEventAt ? (
                <span>Последнее: {fmtRelativeTime(integration.lastEventAt)}</span>
              ) : null}
              {s.mappings.length ? <span>Привязано объектов: {s.mappings.length}</span> : null}
              {s.unmapped.length ? (
                <span className="text-amber">Без привязки: {s.unmapped.length}</span>
              ) : null}
            </div>

            <div className="border-t border-line pt-3">
              {confirm === 'none' ? (
                <div className="flex flex-wrap gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setConfirm('rotate')}>
                    <ArrowsClockwiseIcon weight="bold" className="size-4" /> Обновить секрет
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirm('disconnect')}>
                    <LinkBreakIcon weight="bold" className="size-4" /> Отключить
                  </Button>
                </div>
              ) : confirm === 'rotate' ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-amber">
                    Старая ссылка перестанет работать - брони не будут приходить, пока клиент не
                    вставит новую в кабинет RC.
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => rotateMut.mutate()}
                  >
                    {rotateMut.isPending ? 'Обновляем…' : 'Всё равно обновить'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirm('none')}>
                    Отмена
                  </Button>
                </div>
              ) : (
                // Не «вы уверены?», а список последствий: отключение молча ломает поток броней,
                // и заметить это можно только по пустому календарю через неделю.
                <div className="flex flex-col gap-2">
                  <div className="flex items-start gap-2 rounded-md border border-red/40 bg-red/10 p-3 text-xs text-red">
                    <WarningIcon weight="fill" className="mt-0.5 size-4 shrink-0" />
                    <div className="flex flex-col gap-1">
                      <span>Отключаем RealtyCalendar. После этого:</span>
                      <ul className="list-inside list-disc space-y-0.5">
                        <li>брони из RC перестанут приходить - ни новые, ни отмены</li>
                        {/* Мы отвечаем 200 и на неверный секрет, поэтому RC считает доставку
                            успешной: ретраев не будет, сам вебхук у них не остановится, а
                            тариф «за интеграцию» продолжит списываться. Удалять ссылку в
                            кабинете надо руками. */}
                        <li>
                          ссылку нужно удалить и в кабинете RC: для них она продолжит выглядеть
                          рабочей, а тариф за интеграцию будет списываться дальше
                        </li>
                        {s.mappings.length ? (
                          <li>
                            привязки объектов удалятся ({s.mappings.length} шт.) - при повторном
                            подключении их надо будет сделать заново
                          </li>
                        ) : null}
                      </ul>
                      <span>
                        Уже загруженные брони остаются. Чтобы вернуть поток, нужно будет нажать
                        «Подключить» здесь и вставить новую ссылку в кабинет RC.
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={busy}
                      onClick={() => disconnectMut.mutate()}
                    >
                      {disconnectMut.isPending ? 'Отключаем…' : 'Всё равно отключить'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirm('none')}>
                      Отмена
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
